import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ProductsTab } from '../PartnerMarketplace/ProductsTab/ProductsTab';
import { CataloguesTab } from '../PartnerMarketplace/CataloguesTab/CataloguesTab';
import { ImagesTab } from '../PartnerMarketplace/ImagesTab/ImagesTab';
import { DistributorsTab } from '../PartnerMarketplace/DistributorsTab/DistributorsTab';
import { MicrosoftLicencesTab } from '../PartnerMarketplace/MicrosoftLicencesTab/MicrosoftLicencesTab';
import { CatalogBrowseView } from '../PartnerMarketplace/CatalogBrowseView/CatalogBrowseView';
import { AddProductsDrawer } from '../PartnerMarketplace/CataloguesTab/AddProductsDrawer';
import { ProductDialog, ProductDraft } from '../PartnerMarketplace/ProductsTab/ProductDialog';
// BulkAssignDialog removed - assignments now managed per-customer in Tenants view
import { BulkImportDialog } from '../PartnerMarketplace/ProductsTab/BulkImportDialog';
import { HardwareBulkImportDialog } from '../PartnerMarketplace/ProductsTab/HardwareBulkImportDialog';
import { ImagePicker } from '../components/ui/ImagePicker';
import useMarketplaceState, { ProductLite, invalidateMarketplaceCache } from '../PartnerMarketplace/useMarketplaceState';
import { marketplaceService } from '../services/marketplaceService';
import {
  deleteDistributorProducts,
  approveEnrichment,
  getEnrichment,
  getDistributorSeedSkus,
  getSeedSkus,
  saveDistributorSeedSkus,
  saveSeedSkus,
} from '../services/distributorService';
import {
  deleteTelstraProducts,
  getTelstraSeedSkus,
  saveTelstraSeedSkus,
} from '../services/telstraDistributorService';
import { preloadIcons } from '../services/iconCache';
import { detectVendorPreset, getComboKey } from '../PartnerMarketplace/vendorPresets';
import { microsoftSkuIdToSave } from '../PartnerMarketplace/microsoftLicenceLink';
import { serializeInclusions, normalizeInclusionList } from '../PartnerMarketplace/inclusionItem';
import {
  attributesToFeatureStrings,
  normaliseFeatureAttributes,
} from '../PartnerMarketplace/productFeatureSpecs';

/**
 * Marketplace Configuration (Partner)
 *
 * High-level IA: Products | Catalogues
 * - Products: curate global product set (grid/table, filters, quick actions)
 * - Catalogues: build reusable bundles, publish/unpublish, add products
 * - Assignments: managed per-customer in the Tenants > Marketplace view
 *
 * This entry orchestrates the tabs and reuses existing services. No backend contracts changed.
 */

type TabKey = 'products' | 'catalog' | 'catalogues' | 'images' | 'distributors' | 'microsoft-licences';

// Helper function to format prices with commas and 2 decimal places
const formatPrice = (price: number | undefined): string => {
  if (typeof price !== 'number') return '0.00';
  return price.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const normaliseSku = (sku: string | undefined | null): string => (sku ?? '').trim().toUpperCase();

function isEditableEnrichmentSource(source: unknown): source is 'dickerdata' | 'ingram' {
  return source === 'dickerdata' || source === 'ingram';
}

function isDistributorHardwareDraft(draft: ProductDraft): boolean {
  const source = (draft as any).source;
  const domain = draft.domain;
  return (source === 'dickerdata' || source === 'ingram' || source === 'telstra-distributor') &&
    (domain === 'computers' || domain === 'unified-collaboration' || domain === 'mobile-hardware');
}

function productFeatureFallback(draft: ProductDraft): ProductDraft['features'] {
  if (isDistributorHardwareDraft(draft)) {
    return attributesToFeatureStrings((draft as any).enrichmentAttributes);
  }
  return serializeInclusions(normalizeInclusionList((draft as any).features));
}

function filterSeedSkus<T extends { sku: string }>(items: T[], skusToRemove: Set<string>): { next: T[]; changed: boolean } {
  const next = items.filter((item) => !skusToRemove.has(normaliseSku(item.sku)));
  return { next, changed: next.length !== items.length };
}

export default function PartnerMarketplaceView() {
  const { products, setProducts, catalogues, setCatalogues, searchAll, reload, loading: productsLoading } = useMarketplaceState();

  // Tab routing via query param (?tab=) — syncs with sidebar navigation
  const location = useLocation();
  const validTabs: TabKey[] = ['products', 'catalog', 'catalogues', 'images', 'distributors', 'microsoft-licences'];
  const getTabFromSearch = (search: string): TabKey => {
    const raw = new URLSearchParams(search).get('tab') || 'products';
    if (raw === 'dickerdata' || raw === 'telstra-dist') return 'distributors';
    return validTabs.includes(raw as TabKey) ? (raw as TabKey) : 'products';
  };
  const [tab, setTab] = useState<TabKey>(() => getTabFromSearch(location.search));

  // Sync tab when URL changes (e.g. sidebar navigation)
  useEffect(() => {
    const urlTab = getTabFromSearch(location.search);
    if (urlTab !== tab) setTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Global search
  const [globalQuery, setGlobalQuery] = useState('');
  const globalResults = useMemo(() => {
    const r = searchAll(globalQuery);
    return [
      ...r.products.map((p) => ({ id: p.id, kind: 'product' as const, label: p.name, meta: p.vendorOrBrand })),
      ...r.catalogues.map((c) => ({ id: c.snapshotId, kind: 'catalogue' as const, label: c.name, meta: `${c.itemCount} items` })),
      ...r.customers.map((c) => ({ id: c.id, kind: 'customer' as const, label: c.name, meta: undefined })),
    ];
  }, [globalQuery, searchAll]);


  // Product dialog state (for adding new products)
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productDraft, setProductDraft] = useState<ProductDraft | null>(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productSaveError, setProductSaveError] = useState<string | null>(null);
  const [catalogueNotice, setCatalogueNotice] = useState<string | null>(null);

  // Expanded product details state (for inline editing)
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [expandedProductDetails, setExpandedProductDetails] = useState<ProductDraft | null>(null);
  const [isExpandedLoading, setIsExpandedLoading] = useState(false);

  // Add-products drawer (for catalogues)
  const [addProductsDrawer, setAddProductsDrawer] = useState<{ open: boolean; targetCatalogueId: string | null; existingProductIds: Set<string> }>({ open: false, targetCatalogueId: null, existingProductIds: new Set() });

  // Bulk import dialog
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // Hardware bulk import dialog (Computers & Unified Collaboration)
  const [hardwareBulkImportOpen, setHardwareBulkImportOpen] = useState(false);

  // New catalogue name modal
  const [newCatalogueModal, setNewCatalogueModal] = useState<{ open: boolean; name: string }>({ open: false, name: '' });

  // Product filter from catalogue navigation (to show specific product when navigating from All Products)
  const [productFilterFromCatalogue, setProductFilterFromCatalogue] = useState<string | null>(null);

  // Image picker shared
  const [imagePicker, setImagePicker] = useState<{ open: boolean; target: 'image'; applyTo: 'draft' | 'none'; context: 'dialog' | 'expanded' } | null>(null);

  // Image cache for external URLs (SAS tokens)
  const [imageUrlCache, setImageUrlCache] = useState<Record<string, string>>({});
  // Reverse mapping: blob URL -> original URL (for saving)
  const [imageUrlReverseCache, setImageUrlReverseCache] = useState<Record<string, string>>({});

  // Helper to get cached image URL for marketplace items
  const getCachedImageUrl = useCallback((imageUrl: string | undefined): string | undefined => {
    if (!imageUrl) return undefined;
    
    // If it's an external URL that should be cached, use the cached version
    if (imageUrl.startsWith('http') || imageUrl.startsWith('pack:')) {
      const cached = imageUrlCache[imageUrl];
      if (cached) return cached;
      
      // For pack: URLs, return undefined if not cached yet (prevents CSP errors)
      // The image will show a placeholder until cache is populated
      if (imageUrl.startsWith('pack:')) {
        return undefined;
      }
      
      // For http URLs, return as-is (they might work directly)
      return imageUrl;
    }
    
    // Otherwise return as-is (local asset)
    return imageUrl;
  }, [imageUrlCache]);

  // Helper to get original URL from a cached blob URL
  const getOriginalImageUrl = useCallback((imageUrl: string | undefined): string | undefined => {
    if (!imageUrl) return undefined;
    
    // If it's a blob URL, get the original
    if (imageUrl.startsWith('blob:')) {
      return imageUrlReverseCache[imageUrl] || imageUrl;
    }
    
    // Otherwise return as-is
    return imageUrl;
  }, [imageUrlReverseCache]);

  // Ensure we always have the "All Products" snapshot id
  // ALWAYS fetches fresh from API to avoid stale state issues (especially after imports)
  // Picks the one with the most items if there are duplicates
  const resolveAllProductsSnapshotId = useCallback(async (): Promise<string | null> => {
    try {
      // Always fetch fresh from API to avoid stale state
      const list = await marketplaceService.listPartnerSnapshotsBlob();
      const allProductsCats = (list || []).filter((c) => c.name === 'All Products');
      if (allProductsCats.length > 0) {
        // Pick the one with most items
        const best = allProductsCats.reduce((bestCat, current) => {
          if (current.itemCount > bestCat.itemCount) return current;
          if (current.itemCount === bestCat.itemCount && current.createdAtUtc > bestCat.createdAtUtc) return current;
          return bestCat;
        }, allProductsCats[0]);
        return best.snapshotId;
      }
      // Create if missing, then re-list to obtain id
      await marketplaceService.createPartnerSnapshotBlob({
        name: 'All Products',
        notes: 'Default catalogue containing all your products',
        software: [],
        hardware: [],
        consulting: []
      });
      const list2 = await marketplaceService.listPartnerSnapshotsBlob();
      const final = (list2 || []).find((c) => c.name === 'All Products');
      return final?.snapshotId || null;
    } catch (e) {
      return null;
    }
  }, []);

  // Cache all product images when products change
  useEffect(() => {
    const cacheProductImages = async () => {
      const externalImageUrls: string[] = [];
      
      // Collect all external image URLs from products
      products.forEach(product => {
        if (product.image && typeof product.image === 'string' && (product.image.startsWith('http') || product.image.startsWith('pack:'))) {
          externalImageUrls.push(product.image);
        }
      });
      
      // Preload and cache images if we have any
      if (externalImageUrls.length > 0) {
        try {
          const cachedUrls = await preloadIcons(externalImageUrls);
          setImageUrlCache(prev => ({ ...prev, ...cachedUrls }));
          
          // Build reverse mapping for saving
          const reverseMap: Record<string, string> = {};
          Object.entries(cachedUrls).forEach(([originalUrl, blobUrl]) => {
            reverseMap[blobUrl] = originalUrl;
          });
          setImageUrlReverseCache(prev => ({ ...prev, ...reverseMap }));
        } catch (error) {
          // Image preloading is best-effort
        }
      }
    };
    
    if (products.length > 0) {
      cacheProductImages();
    }
  }, [products]);

  // Domain is now the primary routing field (replaces tags for procurement routing)

  const openAddProduct = useCallback((domain?: string) => {
    const domainMap: Record<string, { kind: ProductDraft['kind'], domain: ProductDraft['domain'] }> = {
      software: { kind: 'software', domain: 'software' },
      consulting: { kind: 'consulting', domain: 'consulting' },
      computers: { kind: 'hardware', domain: 'computers' },
      networking: { kind: 'hardware', domain: 'networking' },
      'mobile-hardware': { kind: 'hardware', domain: 'mobile-hardware' },
      telstra: { kind: 'hardware', domain: 'telstra' },
      'unified-collaboration': { kind: 'hardware', domain: 'unified-collaboration' },
    };
    
    const config = domain && domainMap[domain] ? domainMap[domain] : { kind: 'software' as const, domain: 'software' as const };
    
    setProductDraft({ 
      kind: config.kind, 
      domain: config.domain, 
      name: '', 
      vendorOrBrand: '', 
      description: '', 
      price: 0,
      pricingType: config.kind === 'software' ? 'perUser' : 'lumpSum',
      ...(config.kind === 'software' && {
        term: 12,
        billingCycle: 'monthly'
      }),
      image: ''
    });
    setProductSaveError(null);
    setProductDialogOpen(true);
  }, []);

  const duplicateProduct = useCallback((id: string) => {
    const orig = products.find((p) => p.id === id);
    if (!orig) return;
    
    // Use stored domain field if available, otherwise compute from tags/kind
    const allowedDomains = ['computers','networking','mobile-hardware','telstra','unified-collaboration'] as const;
    const storedDomain = (orig as any).domain;
    const tagDomain = (orig.tags || []).map((t) => (t || '').toLowerCase()).find((t) => (allowedDomains as readonly string[]).includes(t));
    const computedDomain = storedDomain || (orig.kind === 'software' ? 'software' : orig.kind === 'consulting' ? 'consulting' : (tagDomain as any) || 'computers');
    
    const draft: ProductDraft = {
      kind: orig.kind === 'hardware' ? 'hardware' : orig.kind === 'consulting' ? 'consulting' : 'software',
      domain: computedDomain,
      name: `${orig.name} (Copy)`,
      vendorOrBrand: orig.vendorOrBrand,
      description: '',
      price: 0,
      pricingType: orig.kind === 'software' ? 'perUser' : 'lumpSum',
      ...(orig.kind === 'software' && {
        term: 12,
        billingCycle: 'monthly'
      }),
      image: orig.image
    };
    setProductDraft(draft);
    setProductSaveError(null);
    setProductDialogOpen(true);
  }, [products]);

  const toggleProductStatus = useCallback((id: string) => {
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, status: p.status === 'active' ? 'hidden' : 'active' } : p)));
  }, [setProducts]);

  const handleProductExpand = useCallback((id: string) => {
    const p = products.find((x) => x.id === id);
    if (!p) return;

    if (expandedProductId === id) {
      // If already expanded, collapse it
      setExpandedProductId(null);
      setExpandedProductDetails(null);
    } else {
      // Expand this product
      setExpandedProductId(id);
      setIsExpandedLoading(true);
      
      // Use stored domain field if available, otherwise compute from tags/kind
      const allowedDomains = ['computers','networking','mobile-hardware','telstra','unified-collaboration'] as const;
      const storedDomain = (p as any).domain;
      const tagDomain = (p.tags || []).map((t) => (t || '').toLowerCase()).find((t) => (allowedDomains as readonly string[]).includes(t as any));
      const computedDomain = storedDomain || (p.kind === 'software' ? 'software' : p.kind === 'consulting' ? 'consulting' : (tagDomain as any) || 'computers');
      // Vendor presets (Microsoft term grid etc.) only apply to software
      // plans — a Microsoft-branded computer like Surface Pro is hardware
      // and shouldn't get the M365 monthly/annual term combos.
      const detectedPreset = p.kind === 'software'
        ? (p.vendorPreset
            ? { id: p.vendorPreset }
            : detectVendorPreset(p.vendorOrBrand || ''))
        : null;
      const existingTermPricing = p.kind === 'software' ? p.termPricing : undefined;
      let termPricing = existingTermPricing;
      if (detectedPreset && !termPricing) {
        const { VENDOR_PRESETS } = require('../PartnerMarketplace/vendorPresets');
        const fullPreset = VENDOR_PRESETS.find((vp: any) => vp.id === detectedPreset.id);
        if (fullPreset) {
          const basePrice = p.sellPrice ?? (Number((p.priceDisplay || '').replace(/[^0-9.]/g, '')) || 0);
          termPricing = {};
          for (const combo of fullPreset.validCombinations) {
            termPricing[getComboKey(combo.billingCycle, combo.term)] = basePrice;
          }
        }
      }

      const d: ProductDraft = {
        id: p.id,
        kind: p.kind,
        domain: computedDomain as any,
        name: p.name,
        vendorOrBrand: p.vendorOrBrand,
        description: p.description || '',
        pricePerUser: p.kind === 'software' ? (Number((p.priceDisplay || '').replace(/[^0-9.]/g, '')) || undefined) : undefined,
        price: p.sellPrice ?? (Number((p.priceDisplay || '').replace(/[^0-9.]/g, '')) || undefined),
        pricingType: computedDomain === 'mobile-hardware' || computedDomain === 'computers' || computedDomain === 'unified-collaboration' ? 'lumpSum' : (p.pricingType || (p.kind === 'software' ? 'perUser' : 'lumpSum')),
        term: p.term,
        billingCycle: p.billingCycle,
        tags: p.tags || [],
        image: p.image,
        logo: p.kind === 'software' ? p.image : undefined,
        buyPrice: p.buyPrice,
        markupPercent: p.markupPercent,
        rrpExTax: p.rrpExTax,
        source: p.source,
        sku: p.sku,
        vendorPartNumber: (p as any).vendorPartNumber,
        featured: p.featured,
        telstraProductType: (p as any).telstraProductType,
        planInclusions: (p as any).planInclusions,
        inclusionApps: (p as any).inclusionApps,
        deviceEligibility: (p as any).deviceEligibility,
        colour: (p as any).colour,
        capacity: (p as any).capacity,
        availableColours: (p as any).availableColours,
        customQuestions: (p as any).customQuestions,
        features: (p as any).features,
        displayOrder: (p as any).displayOrder,
        subCategory: (p as any).subCategory,
        soh: (p as any).soh,
        vendorPreset: detectedPreset?.id,
        termPricing,
        microsoftSkuId: p.microsoftSkuId,
        // Server-hydrated canonical attributes for distributor SKUs (Computers/UC/Mobile)
        enrichmentAttributes: (p as any).enrichmentAttributes,
      };
      setExpandedProductDetails(d);
      setIsExpandedLoading(false);
    }
  }, [products, expandedProductId]);

  const removeDeletedProductsFromDistributorTracking = useCallback(async (items: ProductLite[]): Promise<string[]> => {
    const warnings: string[] = [];
    const dickerSkus = new Set<string>();
    const ingramSkus = new Set<string>();
    const telstraSkus = new Set<string>();

    for (const item of items) {
      const sku = normaliseSku(item.sku);
      if (!sku) continue;
      if (item.source === 'dickerdata') dickerSkus.add(sku);
      if (item.source === 'ingram') ingramSkus.add(sku);
      if (item.source === 'telstra-distributor') telstraSkus.add(sku);
    }

    if (dickerSkus.size > 0) {
      try {
        const seedConfig = await getSeedSkus();
        const { next, changed } = filterSeedSkus(seedConfig.skus ?? [], dickerSkus);
        if (changed) await saveSeedSkus(next);
        await deleteDistributorProducts(Array.from(dickerSkus));
      } catch (err: any) {
        warnings.push(`Dicker Data sync list: ${err?.message ?? 'cleanup failed'}`);
      }
    }

    if (ingramSkus.size > 0) {
      try {
        const seedConfig = await getDistributorSeedSkus('ingram');
        const { next, changed } = filterSeedSkus(seedConfig.skus ?? [], ingramSkus);
        if (changed) await saveDistributorSeedSkus('ingram', next);
      } catch (err: any) {
        warnings.push(`Ingram sync list: ${err?.message ?? 'cleanup failed'}`);
      }
    }

    if (telstraSkus.size > 0) {
      try {
        const seedConfig = await getTelstraSeedSkus();
        const { next, changed } = filterSeedSkus(seedConfig.skus ?? [], telstraSkus);
        if (changed) await saveTelstraSeedSkus(next);
        await deleteTelstraProducts(Array.from(telstraSkus));
      } catch (err: any) {
        warnings.push(`Telstra sync list: ${err?.message ?? 'cleanup failed'}`);
      }
    }

    return warnings;
  }, []);

  const deleteProduct = useCallback(async (id: string) => {
    const prod = products.find((p) => p.id === id);
    if (!prod) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete "${prod.name}"? This will remove it from all catalogues.`);
    if (!confirmed) return;
    
    try {
      // Get all catalogues to remove the product from each
      const allCatalogues = await marketplaceService.listPartnerSnapshotsBlob();
      
      // Remove from ALL catalogues (including custom ones)
      for (const cat of allCatalogues) {
        try {
          const snapshot = await marketplaceService.getPartnerSnapshotBlob(cat.snapshotId);
          const originalCount = (snapshot.software || []).length + (snapshot.hardware || []).length + ((snapshot as any).consulting || []).length;
          
          const sw = (snapshot.software || []).filter((s: any) => s.id !== id);
          const hw = (snapshot.hardware || []).filter((h: any) => h.id !== id);
          const cs = ((snapshot as any).consulting || []).filter((c: any) => c.id !== id);
          const newCount = sw.length + hw.length + cs.length;
          
          // Only update if the product was actually in this catalogue
          if (newCount < originalCount) {
            await marketplaceService.createPartnerSnapshotBlob({
              snapshotId: snapshot.snapshotId,
              name: snapshot.name,
              notes: (snapshot as any).notes || '',
              software: sw,
              hardware: hw,
              consulting: cs
            });
          }
        } catch (e) {
          // Continue with other catalogues
        }
      }

      const cleanupWarnings = await removeDeletedProductsFromDistributorTracking([prod]);
      
      // Remove from local state
      setProducts((prev) => prev.filter((p) => p.id !== id));
      
      // Reload to update catalogue counts
      await reload(true);
      
      if (cleanupWarnings.length > 0) {
        alert(`Deleted "${prod.name}" from all catalogues, but distributor cleanup needs attention:\n\n${cleanupWarnings.join('\n')}`);
      } else {
        alert(`Successfully deleted "${prod.name}" from all catalogues`);
      }
    } catch (e) {
      alert('Failed to delete product. Please try again.');
    }
  }, [products, removeDeletedProductsFromDistributorTracking, setProducts, reload]);

  const bulkDeleteProducts = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    
    try {
      const idsToDelete = new Set(ids);
      const productsToDelete = products.filter((p) => idsToDelete.has(p.id));
      
      // Get all catalogues to remove products from each
      const allCatalogues = await marketplaceService.listPartnerSnapshotsBlob();
      
      // Remove from ALL catalogues (including custom ones)
      for (const cat of allCatalogues) {
        try {
          const snapshot = await marketplaceService.getPartnerSnapshotBlob(cat.snapshotId);
          const originalCount = (snapshot.software || []).length + (snapshot.hardware || []).length + ((snapshot as any).consulting || []).length;
          
          const sw = (snapshot.software || []).filter((s: any) => !idsToDelete.has(s.id));
          const hw = (snapshot.hardware || []).filter((h: any) => !idsToDelete.has(h.id));
          const cs = ((snapshot as any).consulting || []).filter((c: any) => !idsToDelete.has(c.id));
          const newCount = sw.length + hw.length + cs.length;
          
          // Only update if any products were actually in this catalogue
          if (newCount < originalCount) {
            await marketplaceService.createPartnerSnapshotBlob({
              snapshotId: snapshot.snapshotId,
              name: snapshot.name,
              notes: (snapshot as any).notes || '',
              software: sw,
              hardware: hw,
              consulting: cs
            });
          }
        } catch (e) {
          // Continue with other catalogues
        }
      }

      const cleanupWarnings = await removeDeletedProductsFromDistributorTracking(productsToDelete);
      
      // Remove from local state
      setProducts((prev) => prev.filter((p) => !idsToDelete.has(p.id)));
      
      // Reload to update catalogue counts
      await reload(true);
      
      if (cleanupWarnings.length > 0) {
        alert(`Deleted ${ids.length} product${ids.length > 1 ? 's' : ''} from all catalogues, but distributor cleanup needs attention:\n\n${cleanupWarnings.join('\n')}`);
      } else {
        alert(`Successfully deleted ${ids.length} product${ids.length > 1 ? 's' : ''} from all catalogues`);
      }
    } catch (e) {
      alert('Failed to delete products. Please try again.');
    }
  }, [products, removeDeletedProductsFromDistributorTracking, setProducts, reload]);

  const saveProductDraft = useCallback(async () => {
    if (!productDraft || savingProduct) return;
    setSavingProduct(true);
    setProductSaveError(null);
    try {
      // Always use "All Products" catalogue for persistence (robust resolution)
      const sourceSnapshotId = await resolveAllProductsSnapshotId();
      if (!sourceSnapshotId) {
        throw new Error('Could not open the All Products catalogue.');
      }
      
      // Get original image URL (in case it's a blob URL)
      const imageToSave = getOriginalImageUrl(productDraft.image);
      
      // If editing existing product (has id), update snapshot item; else create new product
      if (productDraft.id) {
        const snapshot = await marketplaceService.getPartnerSnapshotBlob(sourceSnapshotId);
        const toCurrency = (n?: number) => typeof n === 'number' && n > 0 ? n : 0;
        const domain = productDraft.domain;
        
        // CRITICAL: Find which array the product is ACTUALLY in by checking ID
        // Products from DickerData with domain='software' may be in hardware array, not software array
        const productId = productDraft.id;
        const isInSoftwareArray = (snapshot.software || []).some((s: any) => s.id === productId);
        const isInHardwareArray = (snapshot.hardware || []).some((h: any) => h.id === productId);
        const isInConsultingArray = ((snapshot as any).consulting || []).some((c: any) => c.id === productId);
        
        if (isInSoftwareArray) {
          const sw = (snapshot.software || []).map((s: any) => s.id === productDraft.id ? {
            ...s,
            name: productDraft.name,
            vendor: productDraft.vendorOrBrand || s.vendor || s.brand,
            brand: productDraft.vendorOrBrand || s.brand || s.vendor,
            price: toCurrency(productDraft.price),
            pricingType: productDraft.pricingType || s.pricingType || 'perUser',
            ...(productDraft.pricingType !== 'lumpSum' && {
              term: productDraft.term ?? s.term ?? 12,
              billingCycle: productDraft.billingCycle || s.billingCycle || 'monthly'
            }),
            description: productDraft.description ?? s.description,
            image: imageToSave || s.image,
            category: 'General',
            domain,
            subCategory: (productDraft as any).subCategory || null,
            ...(typeof productDraft.displayOrder === 'number' && { displayOrder: productDraft.displayOrder }),
            ...(productDraft.vendorPreset && { vendorPreset: productDraft.vendorPreset }),
            ...(productDraft.termPricing && { termPricing: productDraft.termPricing }),
            microsoftSkuId: microsoftSkuIdToSave(productDraft),
            ...(typeof (productDraft as any).buyPrice === 'number' && { buyPrice: (productDraft as any).buyPrice }),
            ...(typeof (productDraft as any).markupPercent === 'number' && { markupPercent: (productDraft as any).markupPercent }),
            ...(typeof (productDraft as any).rrpExTax === 'number' && { rrpExTax: (productDraft as any).rrpExTax }),
            ...((productDraft as any).source && { source: (productDraft as any).source }),
            ...((productDraft as any).sku && { sku: (productDraft as any).sku }),
            ...((productDraft as any).vendorPartNumber && { vendorPartNumber: (productDraft as any).vendorPartNumber }),
            ...(typeof (productDraft as any).soh === 'number' && { soh: (productDraft as any).soh }),
           } : s);
          await marketplaceService.createPartnerSnapshotBlob({ snapshotId: snapshot.snapshotId, name: snapshot.name, notes: (snapshot as any).notes || '', software: sw, hardware: snapshot.hardware || [], consulting: (snapshot as any).consulting || [] });
         } else if (isInConsultingArray) {
           const cs = ((snapshot as any).consulting || []).map((c: any) => c.id === productDraft.id ? {
             ...c,
             name: productDraft.name,
             brand: productDraft.vendorOrBrand || c.brand,
             price: toCurrency(productDraft.price),
             pricingType: productDraft.pricingType || c.pricingType || 'lumpSum',
             ...(productDraft.pricingType !== 'lumpSum' && {
               term: productDraft.term ?? c.term ?? 12,
               billingCycle: productDraft.billingCycle || c.billingCycle || 'monthly'
             }),
             description: productDraft.description ?? c.description,
             image: imageToSave ?? c.image,
             category: 'consulting',
             domain,
             customQuestions: productDraft.customQuestions ?? c.customQuestions,
             // Display order for sorting products
             ...(typeof productDraft.displayOrder === 'number' && { displayOrder: productDraft.displayOrder }),
           } : c);
           await marketplaceService.createPartnerSnapshotBlob({ snapshotId: snapshot.snapshotId, name: snapshot.name, notes: (snapshot as any).notes || '', software: snapshot.software || [], hardware: snapshot.hardware || [], consulting: cs });
         } else if (isInHardwareArray) {
          const hw = (snapshot.hardware || []).map((h: any) => h.id === productDraft.id ? {
            ...h,
            name: productDraft.name,
            brand: productDraft.vendorOrBrand || h.brand,
            category: domain || h.category || 'computers',
            price: toCurrency(productDraft.price),
            // Mobile Hardware, Computers, and Unified Collaboration are always lumpSum, Telstra is always perUser (casual)
            pricingType: domain === 'mobile-hardware' || domain === 'computers' || domain === 'unified-collaboration' ? 'lumpSum' : domain === 'telstra' ? 'perUser' : (productDraft.pricingType || h.pricingType || 'lumpSum'),
            // Telstra plans are always casual (term: 0)
            ...(domain === 'telstra' && { term: 0, billingCycle: 'monthly' }),
            ...(productDraft.pricingType !== 'lumpSum' && domain !== 'mobile-hardware' && domain !== 'computers' && domain !== 'unified-collaboration' && domain !== 'telstra' && {
              term: productDraft.term ?? h.term ?? 12,
              billingCycle: productDraft.billingCycle || h.billingCycle || 'monthly'
            }),
             description: productDraft.description ?? h.description,
             image: imageToSave ?? h.image,
            // Colour and capacity only apply to mobile-hardware domain
            ...(domain === 'mobile-hardware' && { colour: productDraft.colour || 'N/A' }),
            ...(domain === 'mobile-hardware' && { capacity: productDraft.capacity || 'N/A' }),
            // Available colours for mobile-hardware (customer selects at checkout) - always save for mobile-hardware
            ...(domain === 'mobile-hardware' && { availableColours: (productDraft.availableColours || []).filter(c => c.trim()) }),
            domain,
            ...(productDraft.vendorPreset && { vendorPreset: productDraft.vendorPreset }),
            ...(productDraft.termPricing && { termPricing: productDraft.termPricing }),
            microsoftSkuId: microsoftSkuIdToSave(productDraft),
            // Telstra-specific fields
            ...(domain === 'telstra' && productDraft.telstraProductType && { telstraProductType: productDraft.telstraProductType }),
            // Plan inclusions for Telstra products - always save for telstra domain
            ...(domain === 'telstra' && { planInclusions: serializeInclusions(normalizeInclusionList(productDraft.planInclusions)) }),
            // Features for computers and unified-collaboration domains - always save
            ...((domain === 'computers' || domain === 'unified-collaboration') && { features: serializeInclusions(normalizeInclusionList((productDraft as any).features)) }),
            // Sub-category for computers, networking, unified-collaboration, and software - always save to ensure sync
            ...((domain === 'computers' || domain === 'networking' || domain === 'unified-collaboration' || domain === 'software') && { subCategory: (productDraft as any).subCategory || null }),
            // Mobile Hardware device eligibility - always save for mobile-hardware domain
            ...(domain === 'mobile-hardware' && { deviceEligibility: productDraft.deviceEligibility || 'outright_only' }),
            // Display order for sorting products
            ...(typeof productDraft.displayOrder === 'number' && { displayOrder: productDraft.displayOrder }),
            // Preserve distributor pricing fields
            ...(typeof (productDraft as any).buyPrice === 'number' && { buyPrice: (productDraft as any).buyPrice }),
            ...(typeof (productDraft as any).markupPercent === 'number' && { markupPercent: (productDraft as any).markupPercent }),
            ...(typeof (productDraft as any).rrpExTax === 'number' && { rrpExTax: (productDraft as any).rrpExTax }),
            ...((productDraft as any).source && { source: (productDraft as any).source }),
            ...((productDraft as any).sku && { sku: (productDraft as any).sku }),
            ...((productDraft as any).vendorPartNumber && { vendorPartNumber: (productDraft as any).vendorPartNumber }),
            ...(typeof (productDraft as any).soh === 'number' && { soh: (productDraft as any).soh }),
          } : h);
          await marketplaceService.createPartnerSnapshotBlob({ snapshotId: snapshot.snapshotId, name: snapshot.name, notes: (snapshot as any).notes || '', software: snapshot.software || [], hardware: hw, consulting: (snapshot as any).consulting || [] });
         } else {
           // Product not found in any array - shouldn't happen
           alert('Error: Product not found in catalogue. Please refresh and try again.');
           return;
         }
      // Update local list as well
        setProducts((prev) => prev.map((p) => p.id === productDraft.id ? {
          ...p,
          name: productDraft.name,
          vendorOrBrand: productDraft.vendorOrBrand,
          priceDisplay: (() => {
            if (productDraft.vendorPreset && productDraft.termPricing) {
              const m2mPrice = productDraft.termPricing[getComboKey('monthly', 0)];
              if (typeof m2mPrice === 'number' && m2mPrice > 0) return `$${formatPrice(m2mPrice)}/user (M2M)`;
            }
            return typeof productDraft.price === 'number' && productDraft.price > 0
              ? (productDraft.pricingType === 'perUser' ? `$${formatPrice(productDraft.price)}/user` : `$${formatPrice(productDraft.price)}`)
              : undefined;
          })(),
          vendorPreset: productDraft.vendorPreset,
          termPricing: productDraft.termPricing,
          microsoftSkuId: microsoftSkuIdToSave(productDraft),
          image: imageToSave,
          tags: (() => {
            const rest = (productDraft.tags || p.tags || []).filter((t) => (t || '').toLowerCase() !== domain); 
            return [domain, ...rest];
          })(),
          domain,
          // Include Telstra and mobile-hardware specific fields
          telstraProductType: domain === 'telstra' ? productDraft.telstraProductType : undefined,
          planInclusions: domain === 'telstra' ? serializeInclusions(normalizeInclusionList(productDraft.planInclusions)) : undefined,
          // Features for computers and unified-collaboration domains
          features: (domain === 'computers' || domain === 'unified-collaboration') ? serializeInclusions(normalizeInclusionList((productDraft as any).features)) : undefined,
          deviceEligibility: domain === 'mobile-hardware' ? (productDraft.deviceEligibility || 'outright_only') : undefined,
          // Colour and capacity only apply to mobile-hardware domain
          colour: domain === 'mobile-hardware' ? (productDraft.colour || 'N/A') : undefined,
          capacity: domain === 'mobile-hardware' ? (productDraft.capacity || 'N/A') : undefined,
          // Available colours for mobile-hardware
          availableColours: domain === 'mobile-hardware' ? (productDraft.availableColours || []).filter(c => c.trim()) : undefined,
          // Display order
          displayOrder: productDraft.displayOrder,
          // Sub-category for filtering
          subCategory: (productDraft as any).subCategory,
          // Distributor pricing fields
          buyPrice: (productDraft as any).buyPrice ?? p.buyPrice,
          markupPercent: (productDraft as any).markupPercent ?? p.markupPercent,
          rrpExTax: (productDraft as any).rrpExTax ?? p.rrpExTax,
          source: (productDraft as any).source ?? p.source,
          sku: (productDraft as any).sku ?? p.sku,
          vendorPartNumber: (productDraft as any).vendorPartNumber ?? (p as any).vendorPartNumber,
          soh: (productDraft as any).soh ?? p.soh,
        } : p));
      } else {
        // Create new product and add to "All Products" catalogue
        const id = `draft-${Date.now()}`;
        const snapshot = await marketplaceService.getPartnerSnapshotBlob(sourceSnapshotId);
        const toCurrency = (n?: number) => typeof n === 'number' && n > 0 ? n : 0;
        const domain = productDraft.domain;

        if (domain === 'software' || productDraft.kind === 'software') {
          const newSoftware: any = {
            id,
            name: productDraft.name,
            brand: productDraft.vendorOrBrand || '',  // Use 'brand' to match backend CatalogProduct model
            price: toCurrency(productDraft.price),
            pricingType: productDraft.pricingType || 'perUser',
            description: productDraft.description || '',
            image: productDraft.image || '',
            category: 'General',
            enabled: true,
            domain,
            source: 'manual',
          };
          if (productDraft.pricingType !== 'lumpSum') {
            newSoftware.term = productDraft.term ?? 12;
            newSoftware.billingCycle = productDraft.billingCycle || 'monthly';
          }
          // Sub-category for software (Productivity/Security)
          if ((productDraft as any).subCategory) {
            newSoftware.subCategory = (productDraft as any).subCategory;
          }
          if (productDraft.colour) newSoftware.colour = productDraft.colour;
          if (productDraft.capacity) newSoftware.capacity = productDraft.capacity;
          if (typeof productDraft.displayOrder === 'number') newSoftware.displayOrder = productDraft.displayOrder;
          if (productDraft.vendorPreset) newSoftware.vendorPreset = productDraft.vendorPreset;
          if (productDraft.termPricing) newSoftware.termPricing = productDraft.termPricing;
          const linkedSkuId = microsoftSkuIdToSave(productDraft);
          if (linkedSkuId) newSoftware.microsoftSkuId = linkedSkuId;
          await marketplaceService.createPartnerSnapshotBlob({
            snapshotId: snapshot.snapshotId,
            name: snapshot.name,
            notes: (snapshot as any).notes || '',
            software: [...(snapshot.software || []), newSoftware],
            hardware: snapshot.hardware || [],
            consulting: (snapshot as any).consulting || []
          });
        } else if (domain === 'consulting' || productDraft.kind === 'consulting') {
          const newConsulting: any = {
            id,
            name: productDraft.name,
            brand: productDraft.vendorOrBrand || '',
            price: toCurrency(productDraft.price),
            pricingType: productDraft.pricingType || 'lumpSum',
            description: productDraft.description || '',
            image: productDraft.image || '',
            category: 'consulting',
            enabled: true,
            domain,
            source: 'manual',
          };
          if (productDraft.pricingType !== 'lumpSum') {
            newConsulting.term = productDraft.term ?? 12;
            newConsulting.billingCycle = productDraft.billingCycle || 'monthly';
          }
          if (productDraft.colour) newConsulting.colour = productDraft.colour;
          if (productDraft.capacity) newConsulting.capacity = productDraft.capacity;
          if (productDraft.customQuestions && productDraft.customQuestions.length > 0) {
            newConsulting.customQuestions = productDraft.customQuestions;
          }
          if (typeof productDraft.displayOrder === 'number') newConsulting.displayOrder = productDraft.displayOrder;
          await marketplaceService.createPartnerSnapshotBlob({
            snapshotId: snapshot.snapshotId,
            name: snapshot.name,
            notes: (snapshot as any).notes || '',
            software: snapshot.software || [],
            hardware: snapshot.hardware || [],
            consulting: [...((snapshot as any).consulting || []), newConsulting]
          });
        } else {
          const newHardware: any = {
            id,
            name: productDraft.name,
            brand: productDraft.vendorOrBrand || '',
            category: domain || 'computers',
            price: toCurrency(productDraft.price),
            // Mobile Hardware, Computers, and Unified Collaboration are always lumpSum, Telstra is always perUser (casual)
            pricingType: domain === 'mobile-hardware' || domain === 'computers' || domain === 'unified-collaboration' ? 'lumpSum' : domain === 'telstra' ? 'perUser' : (productDraft.pricingType || 'lumpSum'),
            description: productDraft.description || '',
            specs: [],
            image: productDraft.image || '',
            enabled: true,
            domain,
            source: 'manual',
          };
          // Telstra plans are always casual (term: 0, no billing cycle)
          if (domain === 'telstra') {
            newHardware.term = 0;
            newHardware.billingCycle = 'monthly';
          } else if (productDraft.pricingType !== 'lumpSum' && domain !== 'mobile-hardware' && domain !== 'computers' && domain !== 'unified-collaboration') {
            newHardware.term = productDraft.term ?? 12;
            newHardware.billingCycle = productDraft.billingCycle || 'monthly';
          }
          // Colour and capacity only apply to mobile-hardware domain
          if (domain === 'mobile-hardware') {
            newHardware.colour = productDraft.colour || 'N/A';
            newHardware.capacity = productDraft.capacity || 'N/A';
            // Available colours for customer selection at checkout - always save for mobile-hardware
            newHardware.availableColours = (productDraft.availableColours || []).filter(c => c.trim());
          }
          // Telstra-specific fields
          if (domain === 'telstra' && productDraft.telstraProductType) {
            newHardware.telstraProductType = productDraft.telstraProductType;
          }
          // Plan inclusions for Telstra products - always save for telstra domain
          if (domain === 'telstra') {
            newHardware.planInclusions = serializeInclusions(normalizeInclusionList(productDraft.planInclusions));
          }
          // Features for computers and unified-collaboration domains - always save
          if (domain === 'computers' || domain === 'unified-collaboration') {
            newHardware.features = serializeInclusions(normalizeInclusionList((productDraft as any).features));
          }
          // Sub-category for computers, networking, and unified-collaboration
          if ((domain === 'computers' || domain === 'networking' || domain === 'unified-collaboration') && (productDraft as any).subCategory) {
            newHardware.subCategory = (productDraft as any).subCategory;
          }
          // Mobile Hardware device eligibility - always save for mobile-hardware domain
          if (domain === 'mobile-hardware') {
            newHardware.deviceEligibility = productDraft.deviceEligibility || 'outright_only';
          }
          // Display order for sorting
          if (typeof productDraft.displayOrder === 'number') {
            newHardware.displayOrder = productDraft.displayOrder;
          }
          await marketplaceService.createPartnerSnapshotBlob({
            snapshotId: snapshot.snapshotId,
            name: snapshot.name,
            notes: (snapshot as any).notes || '',
            software: snapshot.software || [],
            hardware: [...(snapshot.hardware || []), newHardware],
            consulting: (snapshot as any).consulting || []
          });
        }

        // Add to local products list
        const lite: ProductLite = {
          id,
          name: productDraft.name,
          kind: productDraft.kind,
          vendorOrBrand: productDraft.vendorOrBrand,
          status: 'active',
          priceDisplay: (() => {
            if (productDraft.vendorPreset && productDraft.termPricing) {
              const m2mPrice = productDraft.termPricing[getComboKey('monthly', 0)];
              if (typeof m2mPrice === 'number' && m2mPrice > 0) return `$${formatPrice(m2mPrice)}/user (M2M)`;
            }
            return typeof productDraft.price === 'number' && productDraft.price > 0
              ? (productDraft.pricingType === 'perUser' ? `$${formatPrice(productDraft.price)}/user` : `$${formatPrice(productDraft.price)}`)
              : undefined;
          })(),
          vendorPreset: productDraft.vendorPreset,
          termPricing: productDraft.termPricing,
          microsoftSkuId: microsoftSkuIdToSave(productDraft),
          image: imageToSave,
          tags: (() => {
            const rest = (productDraft.tags || []).filter((t) => (t || '').toLowerCase() !== domain);
            return [domain, ...rest];
          })(),
          domain,
          // Include Telstra and mobile-hardware specific fields
          telstraProductType: domain === 'telstra' ? productDraft.telstraProductType : undefined,
          planInclusions: domain === 'telstra' ? serializeInclusions(normalizeInclusionList(productDraft.planInclusions)) : undefined,
          // Features for computers and unified-collaboration domains
          features: (domain === 'computers' || domain === 'unified-collaboration') ? serializeInclusions(normalizeInclusionList((productDraft as any).features)) : undefined,
          deviceEligibility: domain === 'mobile-hardware' ? (productDraft.deviceEligibility || 'outright_only') : undefined,
          // Colour and capacity only apply to mobile-hardware domain
          colour: domain === 'mobile-hardware' ? (productDraft.colour || 'N/A') : undefined,
          capacity: domain === 'mobile-hardware' ? (productDraft.capacity || 'N/A') : undefined,
          // Available colours for mobile-hardware
          availableColours: domain === 'mobile-hardware' ? (productDraft.availableColours || []).filter(c => c.trim()) : undefined,
          // Display order
          displayOrder: productDraft.displayOrder,
        };
        setProducts((prev) => [lite, ...prev]);
      }
      invalidateMarketplaceCache();
      await reload(true);
      const savedName = productDraft.name;
      setCatalogueNotice(productDraft.id ? `Saved ${savedName}.` : `Added ${savedName} to the catalogue.`);
      setProductFilterFromCatalogue(savedName);
      setProductDialogOpen(false);
      setProductDraft(null);
    } catch (e) {
      setProductSaveError(e instanceof Error ? e.message : 'Could not add this licence to the catalogue.');
    } finally {
      setSavingProduct(false);
    }
  }, [productDraft, savingProduct, setProducts, getOriginalImageUrl, resolveAllProductsSnapshotId, reload]);

  // Sync product updates to all custom catalogues (not "All Products")
  const syncProductToCustomCatalogues = useCallback(async (
    productId: string,
    productKind: 'software' | 'hardware' | 'consulting',
    updatedFields: any,
    allProductsSnapshotId: string
  ) => {
    try {
      // Get all catalogues
      const allCatalogues = await marketplaceService.listPartnerSnapshotsBlob();
      
      // Filter to custom catalogues (exclude "All Products")
      const customCatalogues = allCatalogues.filter(
        c => c.snapshotId !== allProductsSnapshotId && c.name !== 'All Products'
      );
      
      if (customCatalogues.length === 0) {
        return;
      }

      for (const cat of customCatalogues) {
        try {
          const snapshot = await marketplaceService.getPartnerSnapshotBlob(cat.snapshotId);
          let updated = false;
          
          // Check and update in software array
          const sw = (snapshot.software || []).map((s: any) => {
            if (s.id === productId) {
              updated = true;
              return { ...s, ...updatedFields };
            }
            return s;
          });
          
          // Check and update in hardware array
          const hw = (snapshot.hardware || []).map((h: any) => {
            if (h.id === productId) {
              updated = true;
              return { ...h, ...updatedFields };
            }
            return h;
          });
          
          // Check and update in consulting array
          const cs = ((snapshot as any).consulting || []).map((c: any) => {
            if (c.id === productId) {
              updated = true;
              return { ...c, ...updatedFields };
            }
            return c;
          });
          
          if (updated) {
            await marketplaceService.createPartnerSnapshotBlob({
              snapshotId: snapshot.snapshotId,
              name: snapshot.name,
              notes: (snapshot as any).notes || '',
              software: sw,
              hardware: hw,
              consulting: cs
            });
          }
        } catch (e) {
          // Continue with other catalogues
        }
      }
    } catch (e) {
      // Sync to custom catalogues failed
    }
  }, []);

  const saveEnrichmentAttributeEdits = useCallback(async (draft: ProductDraft) => {
    const source = (draft as any).source;
    const sku = (draft as any).sku;
    const rawAttributes = (draft as any).enrichmentAttributes;
    if (!isEditableEnrichmentSource(source) || !sku || !rawAttributes) return;
    const attributes = normaliseFeatureAttributes(rawAttributes);

    const detail = await getEnrichment(source, sku, { live: false });
    if (!detail.enrichment) return;

    await approveEnrichment(source, sku, {
      ...detail.enrichment,
      attributes,
    });
  }, []);

  const saveExpandedProductDraft = useCallback(async () => {
    if (!expandedProductDetails) return;
    try {
      await saveEnrichmentAttributeEdits(expandedProductDetails);

      // Always use "All Products" catalogue for persistence (robust resolution)
      const sourceSnapshotId = await resolveAllProductsSnapshotId();
      if (!sourceSnapshotId) {
        return;
      }
      
      // Get original image URL (in case it's a blob URL)
      const imageToSave = getOriginalImageUrl(expandedProductDetails.image);
      const domain = expandedProductDetails.domain;
      const featureFallback = productFeatureFallback(expandedProductDetails);
      
      // Update snapshot item
      if (expandedProductDetails.id) {
        const snapshot = await marketplaceService.getPartnerSnapshotBlob(sourceSnapshotId);
        const toCurrency = (n?: number) => typeof n === 'number' && n > 0 ? n : 0;
        
        // CRITICAL: Find which array the product is ACTUALLY in by checking ID
        // Products from DickerData with domain='software' may be in hardware array, not software array
        const productId = expandedProductDetails.id;
        const isInSoftwareArray = (snapshot.software || []).some((s: any) => s.id === productId);
        const isInHardwareArray = (snapshot.hardware || []).some((h: any) => h.id === productId);
        const isInConsultingArray = ((snapshot as any).consulting || []).some((c: any) => c.id === productId);
        
        // Determine the actual array to update based on where the product EXISTS, not its domain
        // If product is in hardware array (e.g., DickerData software-domain products), update hardware array
        if (isInSoftwareArray) {
          const sw = (snapshot.software || []).map((s: any) => s.id === expandedProductDetails.id ? {
            ...s,
            name: expandedProductDetails.name,
            vendor: expandedProductDetails.vendorOrBrand || s.vendor || s.brand,
            brand: expandedProductDetails.vendorOrBrand || s.brand || s.vendor,
            price: toCurrency(expandedProductDetails.price),
            pricingType: expandedProductDetails.pricingType || s.pricingType || 'perUser',
            ...(expandedProductDetails.pricingType !== 'lumpSum' && {
              term: expandedProductDetails.term ?? s.term ?? 12,
              billingCycle: expandedProductDetails.billingCycle || s.billingCycle || 'monthly'
            }),
            description: expandedProductDetails.description ?? s.description,
            image: imageToSave || s.image,
            category: 'General',
            domain,
            popular: Boolean(expandedProductDetails.featured),
            subCategory: (expandedProductDetails as any).subCategory || null,
            ...(typeof (expandedProductDetails as any).displayOrder === 'number' && { displayOrder: (expandedProductDetails as any).displayOrder }),
            ...(expandedProductDetails.vendorPreset && { vendorPreset: expandedProductDetails.vendorPreset }),
            ...(expandedProductDetails.termPricing && { termPricing: expandedProductDetails.termPricing }),
            microsoftSkuId: microsoftSkuIdToSave(expandedProductDetails),
            ...(typeof (expandedProductDetails as any).buyPrice === 'number' && { buyPrice: (expandedProductDetails as any).buyPrice }),
            ...(typeof (expandedProductDetails as any).markupPercent === 'number' && { markupPercent: (expandedProductDetails as any).markupPercent }),
            ...(typeof (expandedProductDetails as any).rrpExTax === 'number' && { rrpExTax: (expandedProductDetails as any).rrpExTax }),
            ...((expandedProductDetails as any).source && { source: (expandedProductDetails as any).source }),
            ...((expandedProductDetails as any).sku && { sku: (expandedProductDetails as any).sku }),
            ...((expandedProductDetails as any).vendorPartNumber && { vendorPartNumber: (expandedProductDetails as any).vendorPartNumber }),
            ...(typeof (expandedProductDetails as any).soh === 'number' && { soh: (expandedProductDetails as any).soh }),
          } : s);
          await marketplaceService.createPartnerSnapshotBlob({ snapshotId: snapshot.snapshotId, name: snapshot.name, notes: (snapshot as any).notes || '', software: sw, hardware: snapshot.hardware || [], consulting: (snapshot as any).consulting || [] });
        } else if (isInConsultingArray) {
          const cs = ((snapshot as any).consulting || []).map((c: any) => c.id === expandedProductDetails.id ? {
            ...c,
            name: expandedProductDetails.name,
            brand: expandedProductDetails.vendorOrBrand || c.brand,
            price: toCurrency(expandedProductDetails.price),
            pricingType: expandedProductDetails.pricingType || c.pricingType || 'lumpSum',
            ...(expandedProductDetails.pricingType !== 'lumpSum' && {
              term: expandedProductDetails.term ?? c.term ?? 12,
              billingCycle: expandedProductDetails.billingCycle || c.billingCycle || 'monthly'
            }),
            description: expandedProductDetails.description ?? c.description,
            image: imageToSave ?? c.image,
            category: 'consulting',
            domain,
            popular: Boolean(expandedProductDetails.featured),
            // Display order for sorting products
            ...(typeof (expandedProductDetails as any).displayOrder === 'number' && { displayOrder: (expandedProductDetails as any).displayOrder }),
          } : c);
          await marketplaceService.createPartnerSnapshotBlob({ snapshotId: snapshot.snapshotId, name: snapshot.name, notes: (snapshot as any).notes || '', software: snapshot.software || [], hardware: snapshot.hardware || [], consulting: cs });
        } else if (isInHardwareArray) {
          // Hardware array - includes DickerData products mapped to software/consulting domains
          const hw = (snapshot.hardware || []).map((h: any) => h.id === expandedProductDetails.id ? {
            ...h,
            name: expandedProductDetails.name,
            brand: expandedProductDetails.vendorOrBrand || h.brand,
            category: domain || h.category || 'computers',
            price: toCurrency(expandedProductDetails.price),
            // Mobile Hardware, Computers, and Unified Collaboration are always lumpSum, Telstra is always perUser (casual)
            pricingType: domain === 'mobile-hardware' || domain === 'computers' || domain === 'unified-collaboration' ? 'lumpSum' : domain === 'telstra' ? 'perUser' : (expandedProductDetails.pricingType || h.pricingType || 'lumpSum'),
            // Telstra plans are always casual (term: 0)
            ...(domain === 'telstra' && { term: 0, billingCycle: 'monthly' }),
            ...(expandedProductDetails.pricingType !== 'lumpSum' && domain !== 'mobile-hardware' && domain !== 'computers' && domain !== 'unified-collaboration' && domain !== 'telstra' && {
              term: expandedProductDetails.term ?? h.term ?? 12,
              billingCycle: expandedProductDetails.billingCycle || h.billingCycle || 'monthly'
            }),
            description: expandedProductDetails.description ?? h.description,
            image: imageToSave ?? h.image,
            // Colour and capacity only apply to mobile-hardware domain
            ...(domain === 'mobile-hardware' && { colour: expandedProductDetails.colour || 'N/A' }),
            ...(domain === 'mobile-hardware' && { capacity: expandedProductDetails.capacity || 'N/A' }),
            // Available colours for mobile-hardware (customer selects at checkout) - always save for mobile-hardware
            ...(domain === 'mobile-hardware' && { availableColours: ((expandedProductDetails as any).availableColours || []).filter((c: string) => c.trim()) }),
            domain,
            popular: Boolean(expandedProductDetails.featured),
            ...(expandedProductDetails.vendorPreset && { vendorPreset: expandedProductDetails.vendorPreset }),
            ...(expandedProductDetails.termPricing && { termPricing: expandedProductDetails.termPricing }),
            microsoftSkuId: microsoftSkuIdToSave(expandedProductDetails),
            // Preserve distributor pricing fields
            ...(typeof (expandedProductDetails as any).buyPrice === 'number' && { buyPrice: (expandedProductDetails as any).buyPrice }),
            ...(typeof (expandedProductDetails as any).markupPercent === 'number' && { markupPercent: (expandedProductDetails as any).markupPercent }),
            ...(typeof (expandedProductDetails as any).rrpExTax === 'number' && { rrpExTax: (expandedProductDetails as any).rrpExTax }),
            ...((expandedProductDetails as any).source && { source: (expandedProductDetails as any).source }),
            ...((expandedProductDetails as any).sku && { sku: (expandedProductDetails as any).sku }),
            ...((expandedProductDetails as any).vendorPartNumber && { vendorPartNumber: (expandedProductDetails as any).vendorPartNumber }),
            // Telstra-specific fields
            ...((expandedProductDetails as any).telstraProductType && { telstraProductType: (expandedProductDetails as any).telstraProductType }),
            // Plan inclusions for Telstra products - always save for telstra domain (even if empty)
            ...(domain === 'telstra' && { planInclusions: serializeInclusions(normalizeInclusionList((expandedProductDetails as any).planInclusions)) }),
            // Features for computers and unified-collaboration domains - always save (even if empty)
            ...((domain === 'computers' || domain === 'unified-collaboration' || domain === 'mobile-hardware') && { features: featureFallback }),
            // Sub-category for computers, networking, unified-collaboration, and software - ALWAYS save to ensure sync across catalogues
            ...((domain === 'computers' || domain === 'networking' || domain === 'unified-collaboration' || domain === 'software') && { subCategory: (expandedProductDetails as any).subCategory || null }),
            // Mobile Hardware device eligibility for DRO - always save for mobile-hardware domain
            ...(domain === 'mobile-hardware' && { deviceEligibility: (expandedProductDetails as any).deviceEligibility || 'outright_only' }),
            // Display order for sorting products
            ...(typeof (expandedProductDetails as any).displayOrder === 'number' && { displayOrder: (expandedProductDetails as any).displayOrder }),
            // Stock on hand - save for all hardware domains (distributor or manual)
            ...(typeof (expandedProductDetails as any).soh === 'number' && { soh: (expandedProductDetails as any).soh }),
          } : h);
          await marketplaceService.createPartnerSnapshotBlob({ snapshotId: snapshot.snapshotId, name: snapshot.name, notes: (snapshot as any).notes || '', software: snapshot.software || [], hardware: hw, consulting: (snapshot as any).consulting || [] });
        } else {
          // Product not found in any array - this shouldn't happen
          alert('Error: Product not found in catalogue. Please refresh and try again.');
          return;
        }
        
        // Build the updated fields object for syncing to custom catalogues
        const updatedFieldsForSync: any = {
          name: expandedProductDetails.name,
          price: toCurrency(expandedProductDetails.price),
          pricingType: expandedProductDetails.pricingType,
          description: expandedProductDetails.description,
          image: imageToSave,
          domain,
          popular: Boolean(expandedProductDetails.featured),
        };
        
        // Use 'brand' for all product types to match backend CatalogProduct model
        updatedFieldsForSync.brand = expandedProductDetails.vendorOrBrand;
        
        // Add optional fields - colour/capacity only for mobile-hardware
        if (domain === 'mobile-hardware') {
          updatedFieldsForSync.colour = expandedProductDetails.colour || 'N/A';
          updatedFieldsForSync.capacity = expandedProductDetails.capacity || 'N/A';
          // Available colours for mobile-hardware - always save (even if empty, to clear previous values)
          updatedFieldsForSync.availableColours = ((expandedProductDetails as any).availableColours || []).filter((c: string) => c.trim());
        }
        if (expandedProductDetails.pricingType !== 'lumpSum' && domain !== 'mobile-hardware' && domain !== 'computers') {
          updatedFieldsForSync.term = expandedProductDetails.term;
          updatedFieldsForSync.billingCycle = expandedProductDetails.billingCycle;
        }
        // Telstra-specific fields
        if ((expandedProductDetails as any).telstraProductType) {
          updatedFieldsForSync.telstraProductType = (expandedProductDetails as any).telstraProductType;
        }
        // Plan inclusions for Telstra products - always save for telstra domain (even if empty)
        if (domain === 'telstra') {
          updatedFieldsForSync.planInclusions = serializeInclusions(normalizeInclusionList((expandedProductDetails as any).planInclusions));
        }
        // Features for computers and unified-collaboration domains - always include (even if empty)
        if (domain === 'computers' || domain === 'unified-collaboration' || domain === 'mobile-hardware') {
          updatedFieldsForSync.features = featureFallback;
        }
        // Sub-category for computers, networking, unified-collaboration, and software - ALWAYS sync to ensure propagation across catalogues
        if (domain === 'computers' || domain === 'networking' || domain === 'unified-collaboration' || domain === 'software') {
          updatedFieldsForSync.subCategory = (expandedProductDetails as any).subCategory || null;
        }
        // Mobile Hardware device eligibility - always include for mobile-hardware domain
        if (domain === 'mobile-hardware') {
          updatedFieldsForSync.deviceEligibility = (expandedProductDetails as any).deviceEligibility || 'outright_only';
        }
        // Display order for sorting
        if (typeof (expandedProductDetails as any).displayOrder === 'number') {
          updatedFieldsForSync.displayOrder = (expandedProductDetails as any).displayOrder;
        }
        // Sync SKU, SOH, and source for distributor-sourced products
        // These fields are critical for daily price/SOH sync updates
        if ((expandedProductDetails as any).sku) {
          updatedFieldsForSync.sku = (expandedProductDetails as any).sku;
        }
        if ((expandedProductDetails as any).vendorPartNumber) {
          updatedFieldsForSync.vendorPartNumber = (expandedProductDetails as any).vendorPartNumber;
        }
        if ((expandedProductDetails as any).source) {
          updatedFieldsForSync.source = (expandedProductDetails as any).source;
        }
        if (typeof (expandedProductDetails as any).soh === 'number') {
          updatedFieldsForSync.soh = (expandedProductDetails as any).soh;
        }
        if (typeof (expandedProductDetails as any).buyPrice === 'number') {
          updatedFieldsForSync.buyPrice = (expandedProductDetails as any).buyPrice;
        }
        if (typeof (expandedProductDetails as any).markupPercent === 'number') {
          updatedFieldsForSync.markupPercent = (expandedProductDetails as any).markupPercent;
        }
        if (typeof (expandedProductDetails as any).rrpExTax === 'number') {
          updatedFieldsForSync.rrpExTax = (expandedProductDetails as any).rrpExTax;
        }
        if (expandedProductDetails.termPricing) {
          updatedFieldsForSync.termPricing = expandedProductDetails.termPricing;
        }
        if (expandedProductDetails.vendorPreset) {
          updatedFieldsForSync.vendorPreset = expandedProductDetails.vendorPreset;
        }
        // Always present, so removing a link also removes it from custom catalogues.
        updatedFieldsForSync.microsoftSkuId = microsoftSkuIdToSave(expandedProductDetails);
        
        // Sync to custom catalogues (non-blocking, fire and forget)
        syncProductToCustomCatalogues(
          expandedProductDetails.id,
          expandedProductDetails.kind,
          updatedFieldsForSync,
          sourceSnapshotId
        );
        
        // Update local list as well
        setProducts((prev) => prev.map((p) => p.id === expandedProductDetails.id ? {
          ...p,
          name: expandedProductDetails.name,
          vendorOrBrand: expandedProductDetails.vendorOrBrand,
          priceDisplay: (() => {
            if (expandedProductDetails.vendorPreset && expandedProductDetails.termPricing) {
              const m2mPrice = expandedProductDetails.termPricing[getComboKey('monthly', 0)];
              if (typeof m2mPrice === 'number' && m2mPrice > 0) return `$${formatPrice(m2mPrice)}/user (M2M)`;
            }
            return typeof expandedProductDetails.price === 'number' && expandedProductDetails.price > 0
              ? (expandedProductDetails.pricingType === 'perUser' ? `$${formatPrice(expandedProductDetails.price)}/user` : `$${formatPrice(expandedProductDetails.price)}`)
              : undefined;
          })(),
          vendorPreset: expandedProductDetails.vendorPreset,
          termPricing: expandedProductDetails.termPricing,
          microsoftSkuId: microsoftSkuIdToSave(expandedProductDetails),
          image: imageToSave,
          tags: (() => {
            const rest = (expandedProductDetails.tags || p.tags || []).filter((t) => (t || '').toLowerCase() !== domain); 
            return domain ? [domain, ...rest] : rest;
          })(),
          domain,
          // Update pricing fields
          sellPrice: expandedProductDetails.price,
          buyPrice: (expandedProductDetails as any).buyPrice ?? p.buyPrice,
          markupPercent: (expandedProductDetails as any).markupPercent ?? p.markupPercent,
          rrpExTax: (expandedProductDetails as any).rrpExTax ?? p.rrpExTax,
          source: (expandedProductDetails as any).source ?? p.source,
          sku: (expandedProductDetails as any).sku ?? p.sku,
          vendorPartNumber: (expandedProductDetails as any).vendorPartNumber ?? (p as any).vendorPartNumber,
          // Update featured flag
          featured: Boolean(expandedProductDetails.featured),
          // Telstra and mobile-hardware specific fields
          telstraProductType: (expandedProductDetails as any).telstraProductType,
          planInclusions: domain === 'telstra' ? serializeInclusions(normalizeInclusionList((expandedProductDetails as any).planInclusions)) : undefined,
          // Features for computers and unified-collaboration domains
          features: (domain === 'computers' || domain === 'unified-collaboration' || domain === 'mobile-hardware') ? featureFallback : undefined,
          enrichmentAttributes: (expandedProductDetails as any).enrichmentAttributes ?? p.enrichmentAttributes,
          deviceEligibility: domain === 'mobile-hardware' ? ((expandedProductDetails as any).deviceEligibility || 'outright_only') : undefined,
          // Colour and capacity only apply to mobile-hardware domain
          colour: domain === 'mobile-hardware' ? ((expandedProductDetails as any).colour || 'N/A') : undefined,
          capacity: domain === 'mobile-hardware' ? ((expandedProductDetails as any).capacity || 'N/A') : undefined,
          // Available colours for mobile-hardware
          availableColours: domain === 'mobile-hardware' ? ((expandedProductDetails as any).availableColours || []).filter((c: string) => c.trim()) : undefined,
          // Display order
          displayOrder: (expandedProductDetails as any).displayOrder,
          // Sub-category for filtering
          subCategory: (expandedProductDetails as any).subCategory,
          // Stock on hand (for manual products)
          soh: (expandedProductDetails as any).soh,
        } : p));
        
        // Close expanded view and clear filter
        setExpandedProductId(null);
        setExpandedProductDetails(null);
        setProductFilterFromCatalogue(null);
        
        // Reload to ensure sync with backend
        await reload(true);
      }
    } catch (e: any) {
      alert(e?.message || 'Failed to save product changes');
    }
  }, [expandedProductDetails, setProducts, getOriginalImageUrl, resolveAllProductsSnapshotId, reload, syncProductToCustomCatalogues, saveEnrichmentAttributeEdits]);

  const openAddProductsToCatalogue = useCallback(async (snapshotId: string, productIds: string[]) => {
    try {
      // Load the snapshot to get existing product IDs
      const snapshot = await marketplaceService.getPartnerSnapshotBlob(snapshotId);
      const existingIds = new Set<string>();
      
      // Collect IDs from all product arrays
      (snapshot.software || []).forEach((s: any) => existingIds.add(s.id));
      (snapshot.hardware || []).forEach((h: any) => existingIds.add(h.id));
      ((snapshot as any).consulting || []).forEach((c: any) => existingIds.add(c.id));
      
      setAddProductsDrawer({ open: true, targetCatalogueId: snapshotId, existingProductIds: existingIds });
    } catch (e) {
      // Open drawer anyway, but without filtering
      setAddProductsDrawer({ open: true, targetCatalogueId: snapshotId, existingProductIds: new Set() });
    }
  }, []);

  const handleAddProductsConfirm = useCallback(async (ids: string[]) => {
    if (!addProductsDrawer.targetCatalogueId) return;
    try {
      const snapshot = await marketplaceService.getPartnerSnapshotBlob(addProductsDrawer.targetCatalogueId);
      const selected = products.filter((p) => ids.includes(p.id));

      const sw = (snapshot.software || []).slice();
      const hw = (snapshot.hardware || []).slice();
      const cs = ((snapshot as any).consulting || []).slice();

      selected.forEach((p) => {
        const domain = p.domain || (p.kind === 'software' ? 'software' : p.kind === 'consulting' ? 'consulting' : 'computers');
        if (p.kind === 'software') {
          const swItem: any = { id: p.id, name: p.name, brand: p.vendorOrBrand || 'Unknown', category: (p.tags?.[0] as any) || 'General', price: Number((p.priceDisplay || '').replace(/[^0-9.]/g, '')) || 0, pricingType: p.pricingType || 'perUser', term: p.term ?? 12, billingCycle: p.billingCycle || 'monthly', description: p.description || '', image: p.image, enabled: p.status !== 'hidden', domain, source: p.source || 'manual', sku: (p as any).sku, vendorPartNumber: (p as any).vendorPartNumber };
          if ((p as any).vendorPreset) swItem.vendorPreset = (p as any).vendorPreset;
          if ((p as any).termPricing) swItem.termPricing = (p as any).termPricing;
          const linkedSkuId = microsoftSkuIdToSave(p);
          if (linkedSkuId) swItem.microsoftSkuId = linkedSkuId;
          sw.push(swItem);
        } else if (p.kind === 'hardware') {
          hw.push({ 
            id: p.id, 
            name: p.name, 
            brand: p.vendorOrBrand || 'Unknown', 
            category: domain || 'computers', 
            price: Number((p.priceDisplay || '').replace(/[^0-9.]/g, '')) || 0, 
            pricingType: domain === 'telstra' ? 'perUser' : (p.pricingType || 'lumpSum'), 
            term: domain === 'telstra' ? 0 : (p.term ?? 12), 
            billingCycle: p.billingCycle || 'monthly', 
            description: p.description || '', 
            specs: [], 
            image: p.image, 
            enabled: p.status !== 'hidden', 
            domain, 
            source: p.source || 'manual',
            // Include synced product fields (SKU, SOH, pricing)
            sku: (p as any).sku,
            vendorPartNumber: (p as any).vendorPartNumber,
            soh: (p as any).soh,
            buyPrice: (p as any).buyPrice,
            // Include Telstra-specific fields
            telstraProductType: (p as any).telstraProductType,
            planInclusions: (p as any).planInclusions,
            inclusionApps: (p as any).inclusionApps,
            // Include Mobile Hardware device eligibility
            deviceEligibility: (p as any).deviceEligibility,
            // Include optional attributes
            colour: (p as any).colour,
            capacity: (p as any).capacity,
            // Include available colours for mobile-hardware
            availableColours: (p as any).availableColours,
            // Include features and display order
            features: (p as any).features,
            displayOrder: (p as any).displayOrder,
          } as any);
        } else {
          cs.push({ id: p.id, name: p.name, brand: p.vendorOrBrand || 'Service Provider', category: 'consulting', price: Number((p.priceDisplay || '').replace(/[^0-9.]/g, '')) || 0, pricingType: 'lumpSum' as const, term: 12 as const, billingCycle: 'monthly' as const, description: p.description || '', specs: [], image: p.image, enabled: p.status !== 'hidden', domain, source: 'manual' } as any);
        }
      });

      await marketplaceService.createPartnerSnapshotBlob({ snapshotId: snapshot.snapshotId, name: snapshot.name, notes: (snapshot as any).notes || '', software: sw, hardware: hw, consulting: cs });
      setAddProductsDrawer({ open: false, targetCatalogueId: null, existingProductIds: new Set() });
      await reload(true);
    } catch (e) {
      setAddProductsDrawer({ open: false, targetCatalogueId: null, existingProductIds: new Set() });
    }
  }, [addProductsDrawer.targetCatalogueId, products, reload]);

  // Open the new catalogue modal
  const createCatalogue = useCallback(() => {
    setNewCatalogueModal({ open: true, name: '' });
  }, []);

  // Actually create the catalogue when user confirms
  const confirmCreateCatalogue = useCallback(async () => {
    const name = newCatalogueModal.name.trim();
    if (!name) return;
    try {
      await marketplaceService.createPartnerSnapshotBlob({ name, notes: 'Created from Partner Portal', software: [], hardware: [], consulting: [] });
      setNewCatalogueModal({ open: false, name: '' });
      await reload(true);
      setTab('catalogues');
    } catch (e) {
      // Error creating catalogue
    }
  }, [newCatalogueModal.name, reload]);

  // Navigate to Products tab filtered to a specific product (from All Products catalogue)
  const navigateToProduct = useCallback((productId: string, productName: string) => {
    // Set the filter to the product name so it appears in search
    setProductFilterFromCatalogue(productName);
    // Switch to products tab
    setTab('products');
    // Also expand the product
    const product = products.find(p => p.id === productId);
    if (product) {
      handleProductExpand(productId);
    }
  }, [products, handleProductExpand]);

  const duplicateCatalogue = useCallback(async (snapshotId: string) => {
    try {
      const snapshot = await marketplaceService.getPartnerSnapshotBlob(snapshotId);
      await marketplaceService.createPartnerSnapshotBlob({ name: `${snapshot.name} (Copy)`, notes: (snapshot as any).notes || '', software: snapshot.software || [], hardware: snapshot.hardware || [], consulting: (snapshot as any).consulting || [] });
      await reload(true);
    } catch (e) {
      // Error duplicating catalogue
    }
  }, [reload]);

  const archiveCatalogue = useCallback(async (snapshotId: string) => {
    const cat = catalogues.find((c) => c.snapshotId === snapshotId);
    const ok = window.confirm(`Archive "${cat?.name || 'catalogue'}"?`);
    if (!ok) return;
    try {
      await marketplaceService.deletePartnerSnapshotBlob(snapshotId);
      setCatalogues((prev) => prev.filter((c) => c.snapshotId !== snapshotId));
    } catch (e) {
      // Error archiving catalogue
    }
  }, [catalogues, setCatalogues]);

  // DEV ONLY: Permanently delete a catalogue (same as archive but with scarier confirmation)
  const deleteCatalogue = useCallback(async (snapshotId: string) => {
    const cat = catalogues.find((c) => c.snapshotId === snapshotId);
    const ok = window.confirm(`⚠️ PERMANENTLY DELETE "${cat?.name || 'catalogue'}"?\n\nThis action cannot be undone and may break customer assignments.`);
    if (!ok) return;
    try {
      await marketplaceService.deletePartnerSnapshotBlob(snapshotId);
      setCatalogues((prev) => prev.filter((c) => c.snapshotId !== snapshotId));
    } catch (e) {
      // Error deleting catalogue
    }
  }, [catalogues, setCatalogues]);

  const publishToggle = useCallback(async (snapshotId: string, publish: boolean) => {
    try {
      const snapshot = await marketplaceService.getPartnerSnapshotBlob(snapshotId);
      const notes = `${(snapshot as any).notes || ''}`.trim();
      const nextNotes = publish ? `${notes}${notes ? ' ' : ''}[published]` : notes.replace('[published]', '').trim();
      await marketplaceService.createPartnerSnapshotBlob({ snapshotId, name: snapshot.name, notes: nextNotes, software: snapshot.software || [], hardware: snapshot.hardware || [], consulting: (snapshot as any).consulting || [] });
      await reload(true);
    } catch (e) {
      // Error toggling publish state
    }
  }, [reload]);

  const onGlobalResultSelect = useCallback((item: { id: string; kind: 'product' | 'catalogue' | 'customer'; label: string }) => {
    if (item.kind === 'product') setTab('products');
    if (item.kind === 'catalogue') setTab('catalogues');
    // Customer assignments are now managed in Tenants > Marketplace view
    setGlobalQuery('');
  }, []);

  // Bulk assign functionality has been moved to per-customer management in Tenants view

  const handleBulkImport = useCallback(async (products: ProductDraft[]) => {
    try {
      // Always use "All Products" catalogue for persistence (robust resolution)
      const sourceSnapshotId = await resolveAllProductsSnapshotId();
      
      if (!sourceSnapshotId) throw new Error('No "All Products" catalogue found');

      const snapshot = await marketplaceService.getPartnerSnapshotBlob(sourceSnapshotId);
      const toCurrency = (n?: number) => typeof n === 'number' && n > 0 ? n : 0;

      const newSoftware: any[] = [];
      const newHardware: any[] = [];
      const newConsulting: any[] = [];
      const newProductLites: ProductLite[] = [];

      products.forEach((productDraft) => {
        const id = `import-${Date.now()}-${Math.random()}`;

        if (productDraft.kind === 'software') {
          const item: any = {
            id,
            name: productDraft.name,
            brand: productDraft.vendorOrBrand || '',  // Use 'brand' to match backend model
            price: toCurrency(productDraft.price),
            pricingType: productDraft.pricingType || 'perUser',
            description: productDraft.description || '',
            image: productDraft.image || '',
            category: 'General',
            enabled: true,
            source: 'manual',
          };
          if (productDraft.pricingType !== 'lumpSum') {
            item.term = productDraft.term ?? 12;
            item.billingCycle = productDraft.billingCycle || 'monthly';
          }
          if (productDraft.colour) item.colour = productDraft.colour;
          if (productDraft.capacity) item.capacity = productDraft.capacity;
          if (productDraft.vendorPreset) item.vendorPreset = productDraft.vendorPreset;
          if (productDraft.termPricing) item.termPricing = productDraft.termPricing;
          newSoftware.push(item);

          let bulkPriceDisplay: string | undefined;
          if (productDraft.vendorPreset && productDraft.termPricing) {
            const m2mKey = getComboKey('monthly', 0);
            const m2mPrice = productDraft.termPricing[m2mKey];
            bulkPriceDisplay = typeof m2mPrice === 'number' && m2mPrice > 0
              ? `$${formatPrice(m2mPrice)}/user (M2M)` : undefined;
          }
          if (!bulkPriceDisplay) {
            bulkPriceDisplay = typeof productDraft.price === 'number' && productDraft.price > 0
              ? (productDraft.pricingType === 'perUser' ? `$${formatPrice(productDraft.price)}/user` : `$${formatPrice(productDraft.price)}`)
              : undefined;
          }
          newProductLites.push({
            id,
            name: productDraft.name,
            kind: 'software',
            vendorOrBrand: productDraft.vendorOrBrand,
            status: 'active',
            priceDisplay: bulkPriceDisplay,
            image: productDraft.image,
            tags: [productDraft.domain || 'software']
          });
        } else if (productDraft.kind === 'consulting') {
          const item: any = {
            id,
            name: productDraft.name,
            brand: productDraft.vendorOrBrand || '',
            price: toCurrency(productDraft.price),
            pricingType: productDraft.pricingType || 'lumpSum',
            description: productDraft.description || '',
            image: productDraft.image || '',
            category: 'consulting',
            enabled: true
          };
          if (productDraft.pricingType !== 'lumpSum') {
            item.term = productDraft.term ?? 12;
            item.billingCycle = productDraft.billingCycle || 'monthly';
          }
          if (productDraft.colour) item.colour = productDraft.colour;
          if (productDraft.capacity) item.capacity = productDraft.capacity;
          newConsulting.push(item);

          newProductLites.push({
            id,
            name: productDraft.name,
            kind: 'consulting',
            vendorOrBrand: productDraft.vendorOrBrand,
            status: 'active',
            priceDisplay: typeof productDraft.price === 'number' && productDraft.price > 0
              ? (productDraft.pricingType === 'perUser' ? `$${formatPrice(productDraft.price)}/user` : `$${formatPrice(productDraft.price)}`)
              : undefined,
            image: productDraft.image,
            tags: ['consulting']
          });
        } else {
          const item: any = {
            id,
            name: productDraft.name,
            brand: productDraft.vendorOrBrand || '',
            category: productDraft.domain || 'computers',
            price: toCurrency(productDraft.price),
            pricingType: productDraft.pricingType || 'lumpSum',
            description: productDraft.description || '',
            specs: [],
            image: productDraft.image || '',
            enabled: true,
            domain: productDraft.domain || 'computers',
          };
          if (productDraft.pricingType !== 'lumpSum') {
            item.term = productDraft.term ?? 12;
            item.billingCycle = productDraft.billingCycle || 'monthly';
          }
          if (productDraft.colour) item.colour = productDraft.colour;
          if (productDraft.capacity) item.capacity = productDraft.capacity;
          newHardware.push(item);

          newProductLites.push({
            id,
            name: productDraft.name,
            kind: 'hardware',
            vendorOrBrand: productDraft.vendorOrBrand,
            status: 'active',
            priceDisplay: typeof productDraft.price === 'number' && productDraft.price > 0
              ? (productDraft.pricingType === 'perUser' ? `$${formatPrice(productDraft.price)}/user` : `$${formatPrice(productDraft.price)}`)
              : undefined,
            image: productDraft.image,
            tags: [productDraft.domain || 'computers'],
            domain: productDraft.domain || 'computers'
          });
        }
      });

      // Save to "All Products" catalogue
      await marketplaceService.createPartnerSnapshotBlob({
        snapshotId: snapshot.snapshotId,
        name: snapshot.name,
        notes: (snapshot as any).notes || '',
        software: [...(snapshot.software || []), ...newSoftware],
        hardware: [...(snapshot.hardware || []), ...newHardware],
        consulting: [...((snapshot as any).consulting || []), ...newConsulting]
      });

      // Update local products list
      setProducts((prev) => [...newProductLites, ...prev]);

      setBulkImportOpen(false);
      alert(`Successfully imported ${products.length} product${products.length !== 1 ? 's' : ''}!`);
    } catch (e) {
      throw e;
    }
  }, [setProducts, resolveAllProductsSnapshotId]);

  // Hardware bulk import handler for Computers and Unified Collaboration
  const handleHardwareBulkImport = useCallback(async (
    products: ProductDraft[],
    domain: 'computers' | 'unified-collaboration'
  ) => {
    try {
      // Always use "All Products" catalogue for persistence (robust resolution)
      const sourceSnapshotId = await resolveAllProductsSnapshotId();
      
      if (!sourceSnapshotId) throw new Error('No "All Products" catalogue found');

      const snapshot = await marketplaceService.getPartnerSnapshotBlob(sourceSnapshotId);
      const toCurrency = (n?: number) => typeof n === 'number' && n > 0 ? n : 0;

      const newHardware: any[] = [];
      const newProductLites: ProductLite[] = [];

      products.forEach((productDraft) => {
        const id = `import-${Date.now()}-${Math.random().toString(36).substring(7)}`;

        const item: any = {
          id,
          name: productDraft.name,
          brand: productDraft.vendorOrBrand || '',
          category: domain,
          price: toCurrency(productDraft.price),
          pricingType: 'lumpSum', // Always lumpSum for computers and unified-collaboration
          description: productDraft.description || '',
          specs: [],
          image: '', // No image from bulk import
          enabled: true,
          domain,
          source: 'manual',
          // Include features for computers and unified-collaboration
          features: serializeInclusions(normalizeInclusionList(productDraft.features)),
          // Sub-category for filtering
          subCategory: productDraft.subCategory,
          // Display order for sorting
          ...(typeof productDraft.displayOrder === 'number' && { displayOrder: productDraft.displayOrder }),
          // SKU for product identification
          ...(productDraft.sku && { sku: productDraft.sku }),
          // Stock on hand
          ...(typeof (productDraft as any).soh === 'number' && { soh: (productDraft as any).soh }),
        };
        
        newHardware.push(item);

        newProductLites.push({
          id,
          name: productDraft.name,
          kind: 'hardware',
          vendorOrBrand: productDraft.vendorOrBrand,
          status: 'active',
          priceDisplay: typeof productDraft.price === 'number' && productDraft.price > 0
            ? `$${formatPrice(productDraft.price)}`
            : undefined,
          image: undefined,
          tags: [domain],
          domain,
          source: 'manual',
          features: serializeInclusions(normalizeInclusionList(productDraft.features)),
          subCategory: productDraft.subCategory,
          displayOrder: productDraft.displayOrder,
          sku: productDraft.sku,
          soh: (productDraft as any).soh,
        });
      });

      // Save to "All Products" catalogue
      await marketplaceService.createPartnerSnapshotBlob({
        snapshotId: snapshot.snapshotId,
        name: snapshot.name,
        notes: (snapshot as any).notes || '',
        software: snapshot.software || [],
        hardware: [...(snapshot.hardware || []), ...newHardware],
        consulting: (snapshot as any).consulting || []
      });

      // Update local products list
      setProducts((prev) => [...newProductLites, ...prev]);

      setHardwareBulkImportOpen(false);
      alert(`Successfully imported ${products.length} ${domain === 'computers' ? 'computer' : 'unified collaboration'} product${products.length !== 1 ? 's' : ''}!`);
    } catch (e) {
      throw e;
    }
  }, [setProducts, resolveAllProductsSnapshotId]);

  const onOpenImagePickerFromDialog = useCallback(() => {
    setImagePicker({ open: true, target: 'image', applyTo: 'draft', context: 'dialog' });
  }, []);

  const onOpenImagePickerFromExpanded = useCallback(() => {
    setImagePicker({ open: true, target: 'image', applyTo: 'draft', context: 'expanded' });
  }, []);

  const onImagePicked = useCallback((url: string) => {
    if (imagePicker?.applyTo === 'draft') {
      if (imagePicker.context === 'dialog' && productDraft) {
        setProductDraft({ ...productDraft, image: url });
      } else if (imagePicker.context === 'expanded' && expandedProductDetails) {
        setExpandedProductDetails({ ...expandedProductDetails, image: url });
      }
    }
    setImagePicker(null);
  }, [imagePicker, productDraft, expandedProductDetails]);

  // Catalog, Catalogues, Images, and Distributors render full-height with their own sidebar (like Settings)
  if (tab === 'catalog' || tab === 'catalogues' || tab === 'images' || tab === 'distributors' || tab === 'microsoft-licences') {
    return (
      <div className="flex h-full w-full min-w-0 overflow-hidden bg-slate-50">
        <div className="flex w-full min-w-0">
          {tab === 'catalog' && <CatalogBrowseView />}
          {tab === 'catalogues' && (
            <CataloguesTab
              catalogues={catalogues}
              onCreate={createCatalogue}
              onDuplicate={duplicateCatalogue}
              onArchive={archiveCatalogue}
              onDelete={deleteCatalogue}
              onPublishToggle={publishToggle}
              onAddProducts={openAddProductsToCatalogue}
              getCachedImageUrl={getCachedImageUrl}
              onNavigateToProduct={navigateToProduct}
            />
          )}
          {tab === 'images' && <ImagesTab />}
          {tab === 'distributors' && <DistributorsTab onCatalogueChanged={() => { invalidateMarketplaceCache(); void reload(true); }} />}
          {tab === 'microsoft-licences' && <MicrosoftLicencesTab />}
        </div>

        {/* New Catalogue Name Modal */}
        {newCatalogueModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setNewCatalogueModal({ open: false, name: '' })} />
            <div className="relative bg-white rounded-xl shadow-2xl w-[400px] max-w-[90vw] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">New Catalogue</h3>
                <button
                  onClick={() => setNewCatalogueModal({ open: false, name: '' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="Close"
                  aria-label="Close modal"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Catalogue Name</label>
                <input
                  type="text"
                  value={newCatalogueModal.name}
                  onChange={(e) => setNewCatalogueModal(prev => ({ ...prev, name: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCatalogueModal.name.trim()) {
                      confirmCreateCatalogue();
                    }
                    if (e.key === 'Escape') {
                      setNewCatalogueModal({ open: false, name: '' });
                    }
                  }}
                  placeholder="Enter catalogue name..."
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setNewCatalogueModal({ open: false, name: '' })}
                  className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmCreateCatalogue}
                  disabled={!newCatalogueModal.name.trim()}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    newCatalogueModal.name.trim()
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Create Catalogue
                </button>
              </div>
            </div>
          </div>
        )}

        {addProductsDrawer.open && (
          <AddProductsDrawer
            open={addProductsDrawer.open}
            products={products}
            existingProductIds={addProductsDrawer.existingProductIds}
            onClose={() => setAddProductsDrawer({ open: false, targetCatalogueId: null, existingProductIds: new Set() })}
            onConfirm={handleAddProductsConfirm}
            getCachedImageUrl={getCachedImageUrl}
          />
        )}

        {imagePicker?.open && (
        <ImagePicker
            isOpen={imagePicker.open}
            onClose={() => setImagePicker(null)}
            onSelectImage={onImagePicked}
            selectedImage={(() => {
              if (imagePicker.context === 'dialog') {
                return productDraft?.image;
              } else {
                return expandedProductDetails?.image;
              }
            })()}
            category={(() => {
              const draft = imagePicker.context === 'dialog' ? productDraft : expandedProductDetails;
              const domain = (draft as any)?.domain;
              if (domain === 'software') return 'software';
              if (domain === 'consulting') return 'generic';
              if (domain === 'telstra') return 'telco';
              return 'hardware';
            })()}
          title="Select an image"
        />
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Marketplace Configuration</h2>
              <p className="text-sm text-slate-500">Manage products and catalogues for all customers</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative w-80">
              <input
                type="text"
                value={globalQuery}
                onChange={(e) => setGlobalQuery(e.target.value)}
                placeholder="Search products, catalogues..."
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              />
              <svg className="w-4 h-4 absolute left-3 top-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {globalQuery && globalResults.length > 0 && (
                <div role="listbox" aria-label="Global search results" className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-80 overflow-auto">
                  {globalResults.map((r) => (
                    <button
                      key={`${r.kind}:${r.id}`}
                      onClick={() => onGlobalResultSelect(r)}
                      role="option"
                      aria-selected="false"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 focus:bg-slate-50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate mr-2">{r.label}</span>
                        {r.meta && <span className="text-xs text-slate-500">{r.meta}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Action Buttons */}
            {tab === 'products' && (
              <>
                <button onClick={() => setBulkImportOpen(true)} className="px-3 py-2 border border-slate-200 text-slate-700 text-sm bg-white hover:bg-slate-50 rounded-lg transition-colors">
                  Bulk Import
                </button>
                <button onClick={() => setHardwareBulkImportOpen(true)} className="px-3 py-2 border border-slate-200 text-slate-700 text-sm bg-white hover:bg-slate-50 rounded-lg transition-colors">
                  Bulk Import Hardware
                </button>
                <button onClick={() => openAddProduct()} className="px-3 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 rounded-lg transition-colors">
                  Add Product
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full overflow-hidden max-w-[1600px] mx-auto w-full">
          {tab === 'products' && (
            <ProductsTab
              products={products}
              onCreateProduct={openAddProduct}
              onDuplicate={duplicateProduct}
              onToggleStatus={toggleProductStatus}
              onDelete={deleteProduct}
              onBulkDelete={bulkDeleteProducts}
              onOpen={handleProductExpand}
              expandedProductId={expandedProductId}
              expandedProductDetails={expandedProductDetails}
              isExpandedLoading={isExpandedLoading}
              onSaveExpanded={saveExpandedProductDraft}
              onOpenImagePicker={onOpenImagePickerFromExpanded}
              imagePickerOpen={imagePicker?.open === true && imagePicker.context === 'expanded'}
              onUpdateExpanded={setExpandedProductDetails}
              getCachedImageUrl={getCachedImageUrl}
              initialSearchFilter={productFilterFromCatalogue}
              onClearInitialFilter={() => setProductFilterFromCatalogue(null)}
              onRefresh={() => { invalidateMarketplaceCache(); void reload(true); }}
              refreshing={productsLoading}
              notice={catalogueNotice}
              onDismissNotice={() => setCatalogueNotice(null)}
            />
          )}
        </div>
      </div>

      {productDialogOpen && productDraft && (
        <ProductDialog
          open={productDialogOpen}
          draft={productDraft}
          onChange={(d) => setProductDraft(d)}
          onClose={() => { setProductDialogOpen(false); setProductDraft(null); }}
          onSave={saveProductDraft}
          saving={savingProduct}
          saveError={productSaveError}
          onOpenImagePicker={onOpenImagePickerFromDialog}
          onDelete={productDraft.id ? () => deleteProduct(productDraft.id as string) : undefined}
          getCachedImageUrl={getCachedImageUrl}
          otherProducts={products}
        />
      )}

      {addProductsDrawer.open && (
        <AddProductsDrawer
          open={addProductsDrawer.open}
          products={products}
          existingProductIds={addProductsDrawer.existingProductIds}
          onClose={() => setAddProductsDrawer({ open: false, targetCatalogueId: null, existingProductIds: new Set() })}
          onConfirm={handleAddProductsConfirm}
          getCachedImageUrl={getCachedImageUrl}
        />
      )}

      {bulkImportOpen && (
        <BulkImportDialog
          open={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          onConfirm={handleBulkImport}
        />
      )}

      {hardwareBulkImportOpen && (
        <HardwareBulkImportDialog
          open={hardwareBulkImportOpen}
          onClose={() => setHardwareBulkImportOpen(false)}
          onConfirm={handleHardwareBulkImport}
        />
      )}

      {/* New Catalogue Name Modal */}
      {newCatalogueModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setNewCatalogueModal({ open: false, name: '' })} />
          <div className="relative bg-white rounded-xl shadow-2xl w-[400px] max-w-[90vw] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">New Catalogue</h3>
              <button 
                onClick={() => setNewCatalogueModal({ open: false, name: '' })}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Close"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Catalogue Name</label>
              <input
                type="text"
                value={newCatalogueModal.name}
                onChange={(e) => setNewCatalogueModal(prev => ({ ...prev, name: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newCatalogueModal.name.trim()) {
                    confirmCreateCatalogue();
                  }
                  if (e.key === 'Escape') {
                    setNewCatalogueModal({ open: false, name: '' });
                  }
                }}
                placeholder="Enter catalogue name..."
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setNewCatalogueModal({ open: false, name: '' })}
                className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmCreateCatalogue}
                disabled={!newCatalogueModal.name.trim()}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  newCatalogueModal.name.trim()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Create Catalogue
              </button>
            </div>
          </div>
        </div>
      )}

      {imagePicker?.open && (
      <ImagePicker
          isOpen={imagePicker.open}
          onClose={() => setImagePicker(null)}
          onSelectImage={onImagePicked}
          selectedImage={(() => {
            if (imagePicker.context === 'dialog') {
              return productDraft?.image;
            } else {
              return expandedProductDetails?.image;
            }
          })()}
          category={(() => {
            const draft = imagePicker.context === 'dialog' ? productDraft : expandedProductDetails;
            const domain = (draft as any)?.domain;
            if (domain === 'software') return 'software';
            if (domain === 'consulting') return 'generic';
            if (domain === 'telstra') return 'telco';
            // For computers, networking, mobile-hardware domains, all use 'hardware'
            return 'hardware';
          })()}
        title="Select an image"
      />
      )}
    </div>
  );
}
